// Pausing LAMP's agents (the Tenant's Kill Switch): an Owner pulls it from the Cockpit sidebar or by typing
// "pause" in chat, and releases it the same way. While it is pulled, everyone sees why above every page; Runs
// wait at their next step and nothing is sent until LAMP's agents are resumed.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ApiError, type AgentsStatus, type Api } from "../api.js";
import { Dialog } from "../Dialog.js";
import { CATALOGUES, useWords, type Words } from "../i18n/words.js";

const MAX_REASON_LENGTH = 500;
/** The reason recorded when an Owner pauses without giving one, in English; in their language it is `killSwitchDefaultReason`. */
export const DEFAULT_REASON = CATALOGUES.en.killSwitchDefaultReason;

/** A chat command for the Kill Switch: "pause" (optionally "LAMP's agents", then a reason) or "resume". */
export type AgentsCommand = { readonly kind: "pause"; readonly reason: string } | { readonly kind: "resume" };

const PAUSE = /^\/?pause(?:\s+lamp(?:['’]s)?)?(?:\s+agents)?(?:\s*(?::|-|–|—|\s+because\s)\s*(.*))?$/i;
const RESUME = /^\/?(?:resume|unpause)(?:\s+lamp(?:['’]s)?)?(?:\s+agents)?\s*$/i;

/** The Kill Switch command `text` is, if it is one; a pause without a reason records the default in `words`. */
export function agentsCommand(text: string, words: Words = CATALOGUES.en): AgentsCommand | undefined {
  const message = text.trim();
  if (RESUME.test(message)) return { kind: "resume" };
  const pause = PAUSE.exec(message);
  if (!pause) return undefined;
  const reason = (pause[1] ?? "").trim().slice(0, MAX_REASON_LENGTH);
  return { kind: "pause", reason: reason === "" ? words.killSwitchDefaultReason : reason };
}

/** Whether LAMP's agents are paused, loaded once and replaced after each change. */
export function useAgents(api: Api) {
  const [agents, setAgents] = useState<AgentsStatus | undefined>(undefined);
  useEffect(() => {
    let live = true;
    api.agents().then(
      (status) => live && setAgents(status),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [api]);
  return [agents, setAgents] as const;
}

/** Why LAMP's agents are paused, above every page, for everyone in the business. */
export function AgentsNotice({ agents }: { agents: AgentsStatus | undefined }) {
  const w = useWords();
  if (!agents?.stopped || !agents.notice) return null;
  return (
    <div className="agents-paused" role="status">
      <p>{agents.notice}</p>
      {agents.held > 0 ? <p className="small">{w.killSwitchHeld(agents.held)}</p> : null}
    </div>
  );
}

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError && error.problems.length > 0 ? error.problems.join("; ") : w.killSwitchUnreachable;

/** The sidebar control: an Owner pauses LAMP's agents, after saying why, or resumes them. */
export function PauseAgents({
  api,
  agents,
  onChange,
}: {
  api: Api;
  agents: AgentsStatus | undefined;
  onChange: (agents: AgentsStatus) => void;
}) {
  const w = useWords();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const change = useCallback(
    async (to: () => Promise<AgentsStatus>) => {
      setBusy(true);
      setProblem(undefined);
      try {
        onChange(await to());
        setAsking(false);
        setReason("");
      } catch (error) {
        setProblem(problemOf(error, w));
      } finally {
        setBusy(false);
      }
    },
    [onChange, w],
  );

  if (agents === undefined) return null;
  const stopped = agents.stopped;
  if (stopped && (stopped.byKind !== "owner" || stopped.scope.kind !== "tenant"))
    return <p className="small muted agents-switch">{w.killSwitchByOperations}</p>;

  const pause = (event: FormEvent) => {
    event.preventDefault();
    void change(() => api.pauseAgents(reason.trim() || w.killSwitchDefaultReason));
  };

  return (
    <div className="agents-switch">
      {stopped ? (
        <button type="button" className="btn" disabled={busy} onClick={() => void change(() => api.resumeAgents())}>
          {busy ? w.killSwitchResuming : w.killSwitchResume}
        </button>
      ) : (
        <button type="button" className="btn btn-danger" onClick={() => setAsking(true)}>
          {w.killSwitchPause}
        </button>
      )}
      {problem && !asking ? (
        <p className="alert small" role="alert">
          {problem}
        </p>
      ) : null}
      {asking ? (
        <Dialog title={w.killSwitchPause} onClose={() => setAsking(false)}>
          <form onSubmit={pause}>
            <p>{w.killSwitchExplain}</p>
            <label htmlFor="pause-reason">{w.killSwitchWhy}</label>
            <input
              id="pause-reason"
              value={reason}
              maxLength={MAX_REASON_LENGTH}
              placeholder={w.killSwitchDefaultReason}
              onChange={(event) => setReason(event.target.value)}
            />
            <button type="submit" className="btn btn-danger" disabled={busy}>
              {busy ? w.killSwitchPausing : w.killSwitchPauseNow}
            </button>
            {problem ? (
              <p className="alert" role="alert">
                {problem}
              </p>
            ) : null}
          </form>
        </Dialog>
      ) : null}
    </div>
  );
}
